import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Container, Link as MuiLink, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import CaseStudyNextSteps from '../components/CaseStudyNextSteps';
import { caseStudyPath } from '../data/CaseStudies';
import { insightPath, insights } from '../data/Insights';

function Insights() {
  return (
    <Container
      maxWidth='lg'
      sx={{ pt: { xs: 4, md: 7 }, '& .MuiTypography-root': { overflowWrap: 'anywhere' } }}
    >
      <Box sx={{ maxWidth: 780, mb: 5 }}>
        <Typography
          component='h1'
          variant='h3'
          sx={{ fontWeight: 800, color: '#0B2545', fontSize: { xs: '2rem', md: '3rem' } }}
        >
          CMS and software insights
        </Typography>
        <Typography
          variant='body1'
          sx={{ mt: 2, color: '#475569' }}
        >
          Practical guides for teams planning multi-site content platforms, policy and handbook
          approval workflows, multilingual publishing, accessible authoring, and custom software.
          Each article connects common buyer questions to delivered case studies and our CMS
          capability statement.
        </Typography>
      </Box>

      <Box
        component='nav'
        aria-label='Insights index'
        sx={{ mb: 4 }}
      >
        <Typography
          component='h2'
          variant='h5'
          sx={{ mb: 1, fontWeight: 700, color: '#0B2545' }}
        >
          Explore the articles
        </Typography>
        <Box
          component='ul'
          sx={{ m: 0, pl: 3, display: 'grid', gap: 1 }}
        >
          {insights.map((article) => (
            <li key={article.id}>
              <MuiLink
                component={Link}
                to={insightPath(article.id)}
              >
                {article.title}
              </MuiLink>
            </li>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 3 }}>
        {insights.map((article) => (
          <Box
            component='article'
            key={article.id}
            aria-labelledby={`${article.id}-title`}
            sx={{
              border: '1px solid rgba(11, 37, 69, 0.14)',
              borderRadius: 3,
              minWidth: 0,
              p: { xs: 3, md: 5 },
              bgcolor: '#FFFFFF',
            }}
          >
            <Typography
              variant='overline'
              sx={{ color: '#005596', fontWeight: 700, overflowWrap: 'anywhere' }}
            >
              {article.topic}
            </Typography>
            <Typography
              component='h2'
              id={`${article.id}-title`}
              variant='h4'
              aria-label={`${article.title} — ${article.topic}`}
              sx={{ mt: 1, mb: 2, fontWeight: 700, overflowWrap: 'anywhere' }}
            >
              {article.title}
            </Typography>
            <Typography
              variant='body1'
              sx={{ color: '#475569', maxWidth: '72ch' }}
            >
              {article.summary}
            </Typography>
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: 2,
                bgcolor: '#EFF6FC',
                borderLeft: '4px solid #005596',
              }}
            >
              <Typography
                variant='subtitle2'
                sx={{ fontWeight: 700, color: '#0B2545' }}
              >
                Related case {article.relatedCaseStudies.length === 1 ? 'study' : 'studies'}
              </Typography>
              <Box
                component='ul'
                sx={{ m: 0, mt: 0.5, pl: 2.5, display: 'grid', gap: 0.5 }}
              >
                {article.relatedCaseStudies.map((study) => (
                  <li key={study.studyId}>
                    <MuiLink
                      component={Link}
                      to={caseStudyPath(study.studyId)}
                    >
                      {study.title}
                    </MuiLink>{' '}
                    <Typography
                      component='span'
                      variant='body2'
                      sx={{ color: '#0B2545' }}
                    >
                      ({study.attribution})
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
            <MuiLink
              component={Link}
              to={insightPath(article.id)}
              aria-label={`Read the article: ${article.title}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 3,
                fontWeight: 700,
              }}
            >
              Read the article
              <ArrowForwardIcon fontSize='small' />
            </MuiLink>
          </Box>
        ))}
      </Box>

      <CaseStudyNextSteps />
    </Container>
  );
}

export default Insights;
